import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import {
  Lock,
  ArrowBack,
  CheckCircle,
  Visibility,
  VisibilityOff,
  LockReset,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { MEMBERSHIP_COLORS } from "../components/membership/MembershipStyles";
import { authService } from "../services/authService";

/** Mirrors the backend minimum in AuthService.resetPassword. */
const MIN_PASSWORD_LENGTH = 8;

const SidePanel = styled(Box)(({ theme }) => ({
  height: "100%",
  backgroundColor: MEMBERSHIP_COLORS.bg,
  padding: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const FormPanel = styled(Box)(({ theme }) => ({
  height: "100vh",
  overflowY: "auto",
  padding: theme.spacing(8),
  backgroundColor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(6, 4),
  },
  [theme.breakpoints.down("md")]: {
    height: "auto",
    minHeight: "100vh",
    padding: theme.spacing(4, 3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3, 2),
    paddingTop: "env(safe-area-inset-top, 24px)",
    paddingBottom: "env(safe-area-inset-bottom, 24px)",
  },
}));

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setError("The two passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const passwordAdornment = {
    startAdornment: (
      <InputAdornment position="start">
        <Lock sx={{ color: MEMBERSHIP_COLORS.accent }} />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShowPassword((v) => !v)}
          edge="end"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#FFFFFF" }}>
      <Helmet>
        <title>Reset Password — Dr.Paw</title>
        <meta name="description" content="Choose a new password for your Dr.Paw account." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <HreflangTags />
      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Side: Brand Imagery */}
        <Grid size={{ md: 5, lg: 6 }}>
          <SidePanel>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  color: MEMBERSHIP_COLORS.text,
                  letterSpacing: "-0.04em",
                  mb: 1,
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                Dr.Paw
              </Typography>
            </Box>

            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "20px",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                }}
              >
                <LockReset sx={{ fontSize: 32, color: MEMBERSHIP_COLORS.accent }} />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: MEMBERSHIP_COLORS.text,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Almost <br />
                there.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: MEMBERSHIP_COLORS.secondary,
                  fontSize: "1.1rem",
                  maxWidth: "400px",
                }}
              >
                Pick a new password and you are back in. Resetting signs you out
                everywhere else.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: MEMBERSHIP_COLORS.secondary, opacity: 0.8 }}
              >
                © 2026 Dr.Paw. All rights reserved.
              </Typography>
            </Box>

            {/* Decorative background element */}
            <Box
              sx={{
                position: "absolute",
                top: "20%",
                right: "-10%",
                width: "400px",
                height: "400px",
                backgroundColor: MEMBERSHIP_COLORS.accent,
                borderRadius: "50%",
                opacity: 0.05,
                filter: "blur(100px)",
              }}
            />
          </SidePanel>
        </Grid>

        {/* Right Side: Form */}
        <Grid size={{ xs: 12, md: 7, lg: 6 }}>
          <FormPanel>
            <Container maxWidth="sm">
              <Box mb={4}>
                <Button
                  component={RouterLink}
                  to="/login"
                  startIcon={<ArrowBack />}
                  sx={{
                    color: MEMBERSHIP_COLORS.text,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "transparent", opacity: 0.7 },
                  }}
                >
                  Back to Login
                </Button>
              </Box>

              {isMobile && (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      color: MEMBERSHIP_COLORS.text,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/")}
                  >
                    Dr.Paw
                  </Typography>
                </Box>
              )}

              {!token ? (
                <Box textAlign={isMobile ? "center" : "left"}>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
                    Link Not Valid
                  </Typography>
                  <Typography variant="body1" sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 4 }}>
                    This page needs a reset link from your email. Request a new
                    one and we will send a fresh link.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/forgot-password")}
                    sx={{
                      py: 2,
                      borderRadius: "12px",
                      backgroundColor: MEMBERSHIP_COLORS.text,
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#3D3630" },
                    }}
                  >
                    Request a New Link
                  </Button>
                </Box>
              ) : success ? (
                <Box textAlign={isMobile ? "center" : "left"}>
                  <CheckCircle sx={{ fontSize: { xs: 50, sm: 60 }, color: "#6F8376", mb: 3 }} />
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
                    Password Updated
                  </Typography>
                  <Typography variant="body1" sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 4 }}>
                    You can now log in with your new password.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/login")}
                    sx={{
                      py: 2,
                      borderRadius: "12px",
                      backgroundColor: MEMBERSHIP_COLORS.text,
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#3D3630" },
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: { xs: 4, sm: 6 }, textAlign: isMobile ? "center" : "left" }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: MEMBERSHIP_COLORS.text,
                        mb: 1.5,
                        fontSize: { xs: "1.75rem", sm: "2.125rem" },
                      }}
                    >
                      Set a New Password
                    </Typography>
                    <Typography variant="body1" sx={{ color: MEMBERSHIP_COLORS.secondary }}>
                      Choose something at least {MIN_PASSWORD_LENGTH} characters long.
                    </Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 4, borderRadius: "12px" }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        variant="outlined"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                        InputProps={passwordAdornment}
                      />

                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        variant="outlined"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                        InputProps={passwordAdornment}
                      />

                      <Button
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                          py: 2,
                          borderRadius: "12px",
                          backgroundColor: MEMBERSHIP_COLORS.text,
                          fontSize: "1rem",
                          fontWeight: 800,
                          textTransform: "none",
                          boxShadow: "0 10px 30px rgba(61, 54, 48, 0.2)",
                          "&:hover": {
                            backgroundColor: "#3D3630",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Reset Password"
                        )}
                      </Button>
                    </Stack>
                  </form>
                </>
              )}
            </Container>
          </FormPanel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
